import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Tag, Input, Select, Space, Typography, Card,
  Badge, Statistic, Row, Col, Progress, Tooltip,
} from 'antd';
import {
  SearchOutlined, MailOutlined, CheckCircleOutlined,
  ClockCircleOutlined, SendOutlined, BarChartOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { theme as antdTheme } from 'antd';
import axios from 'axios';
import AdminDateFilter, { resolveRange } from '../../components/AdminDateFilter';

const { Title, Text } = Typography;
const { Option } = Select;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_COLOR = {
  draft:              'default',
  scheduled:          'processing',
  sending:            'warning',
  completed:          'success',
  paused_high_bounce: 'error',
  paused_duplicates:  'error',
};

const AdminCampaigns = () => {
  const { token: tk } = antdTheme.useToken();
  const navigate      = useNavigate();

  const [data, setData]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);
  const [search, setSearch]   = useState('');
  const [statusF, setStatusF] = useState('');
  const [orgFilter, setOrgF]  = useState('');
  const [orgs, setOrgs]               = useState([]);
  const [preset, setPreset]           = useState('All');
  const [customRange, setCustomRange] = useState(null);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const { from, to } = resolveRange(preset, customRange);
      const params = { page: p, limit: 50 };
      if (search)    params.search = search;
      if (statusF)   params.status = statusF;
      if (orgFilter) params.org_id = orgFilter;
      if (from)      params.from   = from;
      if (to)        params.to     = to;
      const res = await axios.get('/api/admin/campaigns', { headers: authHeader(), params });
      setData(res.data.data);
      setTotal(res.data.total);
      setPage(p);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, statusF, orgFilter, preset, customRange]);

  useEffect(() => { load(1); }, [load]);

  // Load orgs for filter dropdown
  useEffect(() => {
    axios.get('/api/admin/orgs', { headers: authHeader() })
      .then(r => setOrgs(r.data))
      .catch(() => {});
  }, []);

  const columns = [
    {
      title: 'Campaign',
      key: 'campaign',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 13 }}>{r.name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.subject}</Text>
        </Space>
      ),
    },
    {
      title: 'Organisation',
      dataIndex: 'org_name',
      key: 'org',
      render: (name, r) => (
        <Text
          style={{ color: tk.colorPrimary, cursor: 'pointer', fontSize: 13 }}
          onClick={() => navigate(`/admin/orgs/${r.org_id}`)}
        >
          {name}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: s => (
        <Tag color={STATUS_COLOR[s] || 'default'}>
          {s?.replace('_', ' ').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Recipients',
      dataIndex: 'total_sent',
      key: 'total_sent',
      width: 100,
      align: 'center',
      render: v => <Text strong>{v.toLocaleString()}</Text>,
    },
    {
      title: 'Open Rate',
      key: 'open_rate',
      width: 120,
      render: (_, r) => r.total_sent > 0 ? (
        <Tooltip title={`${r.opens} opens`}>
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Text style={{ fontSize: 12 }}>{r.open_rate}%</Text>
            <Progress
              percent={parseFloat(r.open_rate)}
              showInfo={false}
              strokeColor="#10b981"
              size="small"
            />
          </Space>
        </Tooltip>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Bounce Rate',
      key: 'bounce_rate',
      width: 120,
      render: (_, r) => {
        const rate = parseFloat(r.bounce_rate);
        const color = rate > 10 ? '#ef4444' : rate > 5 ? '#f59e0b' : '#10b981';
        return r.total_sent > 0 ? (
          <Tooltip title={`${r.bounces} bounced`}>
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              <Text style={{ fontSize: 12, color }}>{r.bounce_rate}%</Text>
              <Progress percent={rate} showInfo={false} strokeColor={color} size="small" />
            </Space>
          </Tooltip>
        ) : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      render: d => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
        </Text>
      ),
    },
  ];

  // Summary stats from loaded data
  const totalSent     = data.reduce((s, r) => s + r.total_sent, 0);
  const totalBounced  = data.reduce((s, r) => s + r.bounces, 0);
  const totalOpened   = data.reduce((s, r) => s + r.opens, 0);

  return (
    <div style={{ padding: '0 2px' }}>
      <div style={{ marginBottom: 20 }}>
        <Title level={4} style={{ marginBottom: 4 }}>All Campaigns</Title>
        <Text type="secondary">Every campaign across all organisations.</Text>
      </div>

      <AdminDateFilter
        preset={preset}
        setPreset={setPreset}
        customRange={customRange}
        setCustomRange={setCustomRange}
        onRefresh={() => load(1)}
        loading={loading}
      />

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {[
          { title: 'Total Campaigns', value: total,        icon: <MailOutlined />,         color: tk.colorPrimary },
          { title: 'Emails Sent',     value: totalSent,    icon: <SendOutlined />,          color: '#10b981' },
          { title: 'Total Opens',     value: totalOpened,  icon: <CheckCircleOutlined />,   color: '#0a40d9' },
          { title: 'Total Bounces',   value: totalBounced, icon: <BarChartOutlined />,      color: totalBounced > 0 ? '#ef4444' : '#9ca3af' },
        ].map(s => (
          <Col xs={12} sm={6} key={s.title}>
            <Card size="small" style={{ borderRadius: 10, border: `1px solid ${tk.colorBorderSecondary}` }}>
              <Statistic
                title={<Text type="secondary" style={{ fontSize: 12 }}>{s.title}</Text>}
                value={s.value}
                valueStyle={{ color: s.color, fontSize: 20, fontWeight: 700 }}
                prefix={React.cloneElement(s.icon, { style: { fontSize: 14, marginRight: 4, color: s.color } })}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Card
        style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}` }}
        styles={{ body: { padding: 0 } }}
      >
        {/* Toolbar */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '14px 20px', borderBottom: `1px solid ${tk.colorBorderSecondary}`,
        }}>
          <Input
            placeholder="Search campaign or subject…"
            prefix={<SearchOutlined style={{ color: tk.colorTextQuaternary }} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            allowClear
            style={{ width: 240, borderRadius: 8 }}
          />
          <Select
            value={statusF || 'all'}
            onChange={v => setStatusF(v === 'all' ? '' : v)}
            style={{ width: 150 }}
          >
            <Option value="all">All Statuses</Option>
            <Option value="draft">Draft</Option>
            <Option value="scheduled">Scheduled</Option>
            <Option value="sending">Sending</Option>
            <Option value="completed">Completed</Option>
            <Option value="paused_high_bounce">Paused — High Bounce</Option>
            <Option value="paused_duplicates">Paused — Duplicates</Option>
          </Select>
          <Select
            value={orgFilter || 'all'}
            onChange={v => setOrgF(v === 'all' ? '' : v)}
            style={{ width: 200 }}
            showSearch
            optionFilterProp="children"
          >
            <Option value="all">All Organisations</Option>
            {orgs.map(o => <Option key={o.id} value={String(o.id)}>{o.name}</Option>)}
          </Select>
        </div>

        <Table
          rowKey="id"
          dataSource={data}
          columns={columns}
          loading={loading}
          pagination={{
            current: page,
            pageSize: 50,
            total,
            onChange: p => load(p),
            showTotal: n => `${n} campaigns`,
            style: { padding: '12px 20px', margin: 0 },
          }}
          style={{ borderRadius: '0 0 12px 12px', overflow: 'hidden' }}
        scroll={{ x: 'max-content' }}
        />
      </Card>
    </div>
  );
};

export default AdminCampaigns;
